import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Lesson, LessonSession } from '../types';
import { sampleLessons } from '../data/sampleLessons';

const LESSONS_COLLECTION = 'lessons';
const SESSIONS_COLLECTION = 'lessonSessions';

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  return db && process.env.REACT_APP_FIREBASE_API_KEY && 
         process.env.REACT_APP_FIREBASE_API_KEY !== 'your_api_key_here';
};

export const lessonService = {
  // Get all lessons
  async getAllLessons(teacherId?: string): Promise<Lesson[]> {
    try {
      if (!isFirebaseConfigured()) {
        // Return sample data if Firebase is not configured
        return sampleLessons;
      }
      
      let q;
      if (teacherId) {
        // Get teacher's own lessons and linked lessons
        q = query(
          collection(db, LESSONS_COLLECTION),
          where('metadata.createdBy', '==', teacherId),
          orderBy('metadata.title', 'asc'),
          orderBy('metadata.subconstruct', 'asc'),
          orderBy('metadata.created_at', 'desc')
        );
      } else {
        // Get all lessons if no teacherId provided
        q = query(
          collection(db, LESSONS_COLLECTION),
          orderBy('metadata.title', 'asc'),
          orderBy('metadata.subconstruct', 'asc'),
          orderBy('metadata.created_at', 'desc')
        );
      }
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        metadata: {
          ...doc.data().metadata,
          lesson_id: doc.id // Use Firebase document ID
        }
      })) as Lesson[];
    } catch (error) {
      console.error('Error fetching lessons:', error);
      // Fallback to sample data on error
      return sampleLessons;
    }
  },

  // Get admin lessons (available to all teachers)
  async getAdminLessons(): Promise<Lesson[]> {
    try {
      if (!isFirebaseConfigured()) {
        // Return sample data if Firebase is not configured
        return sampleLessons.filter(lesson => lesson.metadata.isAdminLesson);
      }
      
      const q = query(
        collection(db, LESSONS_COLLECTION),
        where('metadata.isAdminLesson', '==', true),
        orderBy('metadata.title', 'asc'),
        orderBy('metadata.subconstruct', 'asc'),
        orderBy('metadata.created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        metadata: {
          ...doc.data().metadata,
          lesson_id: doc.id
        }
      })) as Lesson[];
    } catch (error) {
      console.error('Error fetching admin lessons:', error);
      return [];
    }
  },

  // Link a lesson to teacher's account (create a copy)
  async linkLessonToTeacher(lessonId: string, teacherId: string): Promise<Lesson> {
    try {
      if (!isFirebaseConfigured()) {
        throw new Error('Firebase not configured');
      }

      // Get the original lesson
      const originalLesson = await this.getLessonById(lessonId);
      if (!originalLesson) {
        throw new Error('Lesson not found');
      }

      // Create a copy of the lesson for the teacher
      const linkedLesson: Lesson = {
        ...originalLesson,
        metadata: {
          ...originalLesson.metadata,
          lesson_id: '', // Will be set by Firebase
          createdBy: teacherId,
          isAdminLesson: false,
          linkedFrom: lessonId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      };

      // Save the linked lesson
      const docRef = await addDoc(collection(db, LESSONS_COLLECTION), linkedLesson);
      
      // Update the lesson ID
      linkedLesson.metadata.lesson_id = docRef.id;
      
      return linkedLesson;
    } catch (error) {
      console.error('Error linking lesson:', error);
      throw error;
    }
  },

  // Duplicate a lesson (create a copy with modified title)
  async duplicateLesson(lessonId: string, adminId?: string): Promise<Lesson> {
    try {
      if (!isFirebaseConfigured()) {
        throw new Error('Firebase not configured');
      }

      // Get the original lesson
      const originalLesson = await this.getLessonById(lessonId);
      if (!originalLesson) {
        throw new Error('Lesson not found');
      }

      // Create a duplicate of the lesson
      // Remove linkedFrom field if it exists (destructure to exclude it)
      const { linkedFrom, ...metadataWithoutLinkedFrom } = originalLesson.metadata;
      
      const duplicatedLesson: Lesson = {
        ...originalLesson,
        metadata: {
          ...metadataWithoutLinkedFrom,
          lesson_id: '', // Will be set by Firebase
          title: `${originalLesson.metadata.title} (Copy)`,
          createdBy: adminId || originalLesson.metadata.createdBy,
          isAdminLesson: originalLesson.metadata.isAdminLesson, // Preserve admin status
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      };

      // Save the duplicated lesson
      const docRef = await addDoc(collection(db, LESSONS_COLLECTION), duplicatedLesson);
      
      // Update the lesson ID
      duplicatedLesson.metadata.lesson_id = docRef.id;
      
      return duplicatedLesson;
    } catch (error) {
      console.error('Error duplicating lesson:', error);
      throw error;
    }
  },

  // Get lessons by subject
  async getLessonsBySubject(subject: string): Promise<Lesson[]> {
    try {
      const q = query(
        collection(db, LESSONS_COLLECTION),
        where('metadata.subject', '==', subject),
        orderBy('metadata.created_at', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        metadata: {
          ...doc.data().metadata,
          lesson_id: doc.id // Use Firebase document ID
        }
      })) as Lesson[];
    } catch (error) {
      console.error('Error fetching lessons by subject:', error);
      throw error;
    }
  },

  // Get lesson by ID
  async getLessonById(id: string): Promise<Lesson | null> {
    try {
      if (!isFirebaseConfigured()) {
        // Return sample data if Firebase is not configured
        return sampleLessons.find(lesson => lesson.metadata.lesson_id === id) || null;
      }
      
      const docRef = doc(db, LESSONS_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          ...docSnap.data(),
          metadata: {
            ...docSnap.data().metadata,
            lesson_id: docSnap.id // Use Firebase document ID
          }
        } as Lesson;
      }
      return null;
    } catch (error) {
      console.error('Error fetching lesson:', error);
      // Fallback to sample data on error
      return sampleLessons.find(lesson => lesson.metadata.lesson_id === id) || null;
    }
  },

  // Create new lesson
  async createLesson(lesson: Omit<Lesson, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      if (!isFirebaseConfigured()) {
        // Generate a local ID for sample data
        const localId = `local_${Date.now()}`;
        return localId;
      }
      
      const lessonData = {
        ...lesson,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      const docRef = await addDoc(collection(db, LESSONS_COLLECTION), lessonData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating lesson:', error);
      throw error;
    }
  },

  // Update lesson
  async updateLesson(id: string, updates: Partial<Lesson>): Promise<void> {
    try {
      if (!isFirebaseConfigured()) {
        // For local development, just return success
        console.log('Lesson updated locally:', id, updates);
        return;
      }
      
      const docRef = doc(db, LESSONS_COLLECTION, id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating lesson:', error);
      throw error;
    }
  },

  // Update lesson admin status
  async updateLessonAdminStatus(id: string, isAdminLesson: boolean): Promise<void> {
    try {
      if (!isFirebaseConfigured()) {
        console.log('Lesson admin status updated locally:', id, isAdminLesson);
        return;
      }
      
      const docRef = doc(db, LESSONS_COLLECTION, id);
      await updateDoc(docRef, {
        'metadata.isAdminLesson': isAdminLesson,
        'metadata.updated_at': new Date().toISOString(),
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating lesson admin status:', error);
      throw error;
    }
  },

  // Delete lesson
  async deleteLesson(id: string): Promise<void> {
    try {
      const docRef = doc(db, LESSONS_COLLECTION, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting lesson:', error);
      throw error;
    }
  },

  // Create lesson session
  async createLessonSession(session: Omit<LessonSession, 'id'>): Promise<string> {
    try {
      const sessionData = {
        ...session,
        startTime: Timestamp.fromDate(session.startTime),
      };
      const docRef = await addDoc(collection(db, SESSIONS_COLLECTION), sessionData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating lesson session:', error);
      throw error;
    }
  },

  // Update lesson session
  async updateLessonSession(id: string, updates: Partial<LessonSession>): Promise<void> {
    try {
      const docRef = doc(db, SESSIONS_COLLECTION, id);
      const updateData: any = { ...updates };
      
      if (updates.startTime) {
        updateData.startTime = Timestamp.fromDate(updates.startTime);
      }
      if (updates.endTime) {
        updateData.endTime = Timestamp.fromDate(updates.endTime);
      }
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating lesson session:', error);
      throw error;
    }
  },

  // Get lesson sessions by teacher
  async getLessonSessionsByTeacher(teacherId: string): Promise<LessonSession[]> {
    try {
      const q = query(
        collection(db, SESSIONS_COLLECTION),
        where('teacherId', '==', teacherId),
        orderBy('startTime', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startTime: doc.data().startTime?.toDate(),
        endTime: doc.data().endTime?.toDate(),
      })) as LessonSession[];
    } catch (error) {
      console.error('Error fetching lesson sessions:', error);
      throw error;
    }
  },
};
